<template>
    <common-page
        :label="ts( 'openFromCloud' )" :ok-btn="ts( 'importEvent' )" :cancel-btn="ts( 'pickAnotherFile' )" :third-btn="ts( 'importEventOverride' )"
        :use-buttons="preview" :use-third-button="preview && duplicateEvent" @ok="proceed" @cancel="pickAnother" @third="proceed( true )">

        <div v-if="preview">
            <q-markup-table flat>
                <tbody>
                <tr>
                    <td class="text-left text-weight-bold">Verze programu</td>
                    <td>{{ fileVersion }}</td>
                </tr>
                <tr>
                    <td class="text-left text-weight-bold">Čas uložení souboru</td>
                    <td>{{ fileTimestamp }}</td>
                </tr>
                <tr>
                    <td class="text-left text-weight-bold">Data z ČSJ stažena</td>
                    <td>{{ ts( 'whoAndWhen', { who: csjScorer, when: csjTimestamp } ) }}</td>
                </tr>
                <tr>
                    <td class="text-left text-weight-bold">Závod</td>
                    <td>{{ eventDesc }}</td>
                </tr>
                <tr>
                    <td class="text-left text-weight-bold vertical-top">Lodní třídy</td>
                    <td>
                        <q-markup-table flat>
                            <tbody>
                            <tr v-for="( eventClass ) in eventClasses">
                                <td>{{ eventClass.classDesc }}</td>
                                <td class="text-right">{{ tcs( 'boats', eventClass.boats ) }}</td>
                                <td class="text-right">{{ tcs( 'races', eventClass.races ) }}</td>
                            </tr>
                            </tbody>
                        </q-markup-table>
                    </td>
                </tr>
                </tbody>
            </q-markup-table>

            <div class="text-negative q-mt-lg q-mb-lg" v-if="duplicateEvent">
                <q-icon name="error" size="2em" color="negative" />
                <span class="q-ml-sm text-weight-bold">{{ ts( 'duplicateEvent' ) }}</span><br>
            </div>
        </div>

        <q-table flat :rows="storedEvents" :columns="eventColumns" row-key="id" binary-state-sort :no-data-label="ts( 'noEvents' )" v-else>
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <qe-btn
                        color="primary" :label="ts( 'load' )"
                        @click="openEvent( props.row.id )" />
                </q-td>
            </template>
            <template v-slot:body-cell-title="props">
                <q-td :props="props">
                    {{ props.row.eventTitle }}
                    <q-icon name="sailing" class="q-ml-sm" size="1.6em" color="positive" :title="ts( 'finalResultsOnCloud' )" v-if="props.row.finalResults"/>
                </q-td>
            </template>
        </q-table>
    </common-page>
</template>

<script setup lang="ts">
import CommonPage from '@/components/parts/commonPage.vue'
import { authenticate, checkAuth, showError } from "@/network/auth"
import apiRequest from "@/network/httpClient"
import { createEventFromFile } from "@/network/importer"
import { DateFormat, ds, tcs, ts } from '@/plugins/i18n-formatted'
import InfoBox from "@dialogs/InfoBox.vue"
import LogIn from "@dialogs/LogIn.vue"
import { Defaults, Options } from "@model/Setting"
import QeBtn from "@qe/qeBtn.vue"
import EventsRepo from "@repo/EventsRepo"
import SettingsRepo from "@repo/SettingsRepo"
import { useRepo } from "pinia-orm"
import { useQuasar } from 'quasar'
import { computed, onMounted, ref } from "vue"
import { useRoute, useRouter } from 'vue-router'

const quasar = useQuasar()
const router = useRouter()
const route = useRoute()

const openFromCloud = ref( route.params.cloudId )

const storedEvents = ref( [] )
const importedContent = ref( {} )

const preview = ref( false )

const duplicateEvent = ref( false )
const fileVersion = ref( '' )
const fileTimestamp = ref( '' )
const csjTimestamp = ref( '' )
const csjScorer = ref( '' )
const eventDesc = ref( '' )
const eventClasses = ref( [] )

const isAuthenticated = computed( () => ( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) != '' ) )

const eventColumns = [
    {
        name: 'ctlId',
        required: true,
        label: ts( 'ctl' ),
        align: 'center',
        field: row => row.ctlId,
        sortable: true
    },
    {
        name: 'title',
        required: true,
        label: ts( 'eventTitle' ),
        align: 'left',
        field: row => row.eventTitle,
        sortable: true
    },
    {
        name: 'author',
        required: true,
        label: ts( 'author' ),
        align: 'left',
        field: row => String( row.author.regId ).substring( 0, 4 ) + "-" + String( row.author.regId ).substring( 5 ) + " " + row.author.name,
        sortable: true
    },
    {
        name: 'timestamp',
        required: true,
        label: ts( 'savedWhen' ),
        align: 'left',
        field: row => new Date( row.timestamp ).verb(),
        sortable: true
    },
    {
        name: 'actions',
        required: true,
        label: ts( 'actions' ),
        align: 'center',
        field: () => '',
        sortable: false
    }
]

function logIn ( attempts = 3, error = 0 ) {
    quasar.dialog( {
        component: LogIn,
        componentProps: {
            errorMsg: showError( error )
        }
    } ).onOk( async ( payload ) => {
        const lastError = await authenticate( payload.username, payload.password )

        if ( !isAuthenticated.value ) {
            if ( attempts > 1 )
                logIn( attempts - 1, lastError )
            else
                quasar.dialog( {
                    component: InfoBox,
                    componentProps: {
                        message: ts( 'tooManyAttempts' )
                    }
                } )
        } else {
            await performRefresh()
        }
    } )
}

async function performRefresh () {
    apiRequest( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) ).get( '/events/cloud/list' ).then( ( response ) => {
            if ( response.status === 200 ) {
                storedEvents.value = response.data

                if ( openFromCloud.value !== undefined ) {
                    if ( storedEvents.value.some( ( ev ) => ( ev.id === Number( openFromCloud.value ) ) ) ) {
                        openEvent( openFromCloud.value )
                    }
                }
            }
        }
    )
}

async function refresh () {
    if ( await checkAuth() !== 'auth' ) {
        await logIn()

        return
    }

    performRefresh()
}

onMounted( async () => { await refresh() } )

function fillPreview ( content ) {
    preview.value = true

    const eventFrom = content.event.from.toDate()
    const eventTo = content.event.to === null ? null : content.event.to.toDate()

    fileVersion.value = content.version
    fileTimestamp.value = ds( content.generated.toDate(), DateFormat.DateTimeHuman )
    csjTimestamp.value = ds( content.sailingDataTimestamp.toDate(), DateFormat.DateTimeHuman )
    csjScorer.value = content.authorizedScorer
    eventDesc.value = content.event.ctl + ' ' + content.event.title
        + ' (' + ds( eventFrom, ( eventTo != null && !eventFrom.isSameAs( eventTo ) ) ? DateFormat.EventFrom : DateFormat.EventTo )
        + ( ( eventTo == null || eventFrom.isSameAs( eventTo ) ) ? '' : ' – ' + ds( eventTo, DateFormat.EventTo ) ) + ')'

    eventClasses.value = []

    const boats = content.event.boats.reduce( ( acc, cur ) => acc.set( cur.id, cur.boatClassId ), new Map<string, string>( [] ) )

    content.event.classes.forEach( function ( cl ) {
        eventClasses.value.push( {
            classDesc: cl.shortcut,
            weight: cl.weight,
            shortcut: cl.shortcut,
            boats: content.event.boats.filter( b => b.boatClassId === cl.boatClassId ).length,
            races: content.event.races.filter( r => r.results.filter( res => boats.get( res.eventBoatId ) === cl.boatClassId ).length > 0 ).length
        } )
    } )

    eventClasses.value = eventClasses.value.sort( ( a, b ) => a.weight === b.weight ? a.shortcut.localeCompare( b.shortcut ) : b.weight - a.weight )

    duplicateEvent.value = useRepo( EventsRepo ).eventByCtlId( content.event.ctl ) != null
}

function openEvent ( eventId ) {
    apiRequest( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) ).get( '/events/cloud/' + eventId ).then( ( response ) => {
            if ( response.status === 200 ) {
                importedContent.value = {}

                try {
                    importedContent.value = JSON.parse( response.data )
                } catch ( e ) {
                    quasar.notify( {
                        icon: 'error',
                        color: 'negative',
                        message: ts( 'invalidFileFormat', { fileName: '' } ),
                        timeout: 5000
                    } )

                    return
                }

                if ( typeof importedContent.value !== 'object' || !Object.hasOwn( importedContent.value, 'software' ) || importedContent.value.software !== 'Score System' || !Object.hasOwn( importedContent.value, 'version' ) ) {
                    quasar.notify( {
                        icon: 'error',
                        color: 'negative',
                        message: ts( 'notMySaveFile', { fileName: '' } ),
                        timeout: 5000
                    } )

                    return
                }

                fillPreview( importedContent.value )
            }
        }
    )
}

function proceed ( override = false ) {
    preview.value = false
    duplicateEvent.value = false

    if ( override ) {
        useRepo( EventsRepo ).where( 'ctlId', importedContent.value.event.ctl ).get().forEach( ( event ) => useRepo( EventsRepo ).destroy( event.id ) )
    }

    const eventId = createEventFromFile( importedContent.value )

    importedContent.value = {}

    if ( eventId !== undefined )
        router.push( { name: 'EventManager', params: { eventId: eventId } } )
}

function pickAnother () {
    preview.value = false
    duplicateEvent.value = false

    refresh()
}
</script>
